export interface ChartScaleBar {
    low: number;
    high: number;
    close: number;
    volume: number;
}

export interface ChartRange {
    min: number;
    max: number;
}

export interface ChartPriceTick {
    key: string;
    price: number;
    y: number;
}

export const CHART_VIEW = Object.freeze({
    width: 720,
    height: 330,
    left: 16,
    right: 704,
    priceTop: 18,
    priceBottom: 220,
    volumeTop: 246,
    volumeBottom: 314
});

export const CHART_VOLUME_HEIGHT = (
    CHART_VIEW.volumeBottom - CHART_VIEW.volumeTop - 8
);

const VOLUME_SCALE_PERCENTILE = .95;

function number(value: any) {
    const parsed = Number(value || 0);
    return Number.isFinite(parsed) ? parsed : 0;
}

export function stockTickSize(price: number) {
    const value = Math.max(1, Math.floor(number(price)));
    if (value < 2_000) return 1;
    if (value < 5_000) return 5;
    if (value < 20_000) return 10;
    if (value < 50_000) return 50;
    if (value < 200_000) return 100;
    if (value < 500_000) return 500;
    return 1_000;
}

function floorStockTick(price: number) {
    const tick = stockTickSize(price);
    return Math.floor(price / tick) * tick;
}

function ceilStockTick(price: number) {
    const tick = stockTickSize(price);
    return Math.ceil(price / tick) * tick;
}

function nearestStockTick(price: number) {
    const lower = floorStockTick(price);
    const upper = ceilStockTick(price);
    return upper - price <= price - lower ? upper : lower;
}

export function chartRange(
    bars: ChartScaleBar[], currentPrice = 0, levelPrices: number[] = [],
    requiredPrices: number[] = []
): ChartRange {
    if (!bars.length) return { min: 0, max: 1 };
    const lows = bars.map(bar => bar.low);
    const highs = bars.map(bar => bar.high);
    if (currentPrice > 0) {
        lows.push(currentPrice);
        highs.push(currentPrice);
    }
    const anchor = currentPrice || bars[bars.length - 1]?.close || 0;
    const naturalLow = Math.min(...lows);
    const naturalHigh = Math.max(...highs);
    const naturalSpread = Math.max(
        naturalHigh - naturalLow,
        naturalHigh * .002,
        1
    );
    const levelMargin = Math.max(naturalSpread * .35, anchor * .005);
    levelPrices.forEach(price => {
        if (
            price > 0
            && price >= naturalLow - levelMargin
            && price <= naturalHigh + levelMargin
        ) {
            lows.push(price);
            highs.push(price);
        }
    });
    // Trading levels must remain visible even outside the recent candle range.
    requiredPrices.filter(price => Number.isFinite(price) && price > 0).forEach(price => {
        lows.push(price);
        highs.push(price);
    });
    const low = Math.min(...lows);
    const high = Math.max(...highs);
    const spread = Math.max(high - low, high * .002, 1);
    const padding = spread * .09;
    return { min: Math.max(0, low - padding), max: high + padding };
}

export function chartPriceY(price: number, range: ChartRange) {
    const spread = Math.max(range.max - range.min, 1);
    return Number((
        CHART_VIEW.priceBottom - (price - range.min)
        * (CHART_VIEW.priceBottom - CHART_VIEW.priceTop) / spread
    ).toFixed(2));
}

export function chartPriceTicks(range: ChartRange): ChartPriceTick[] {
    const count = 4;
    const spread = range.max - range.min;
    const seen = new Set<number>();
    return Array.from({ length: count }, (_item, index) => {
        const rawPrice = range.max - spread * index / (count - 1);
        if (index === 0) return floorStockTick(rawPrice);
        if (index === count - 1) return ceilStockTick(rawPrice);
        return nearestStockTick(rawPrice);
    }).filter(price => {
        if (
            price <= 0
            || price < range.min
            || price > range.max
            || seen.has(price)
        ) return false;
        seen.add(price);
        return true;
    }).map(price => ({
        key: `tick:${price}`,
        price,
        y: chartPriceY(price, range)
    }));
}

export function chartVolumeScale(bars: ChartScaleBar[]) {
    const values = bars.map(bar => bar.volume)
        .filter(value => value > 0)
        .sort((left, right) => left - right);
    if (!values.length) return 1;
    const index = Math.min(
        values.length - 1,
        Math.max(0, Math.ceil(values.length * VOLUME_SCALE_PERCENTILE) - 1)
    );
    return Math.max(values[index], 1);
}
